import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaCopy, FaTimes, FaCheckCircle, FaWallet, FaInfoCircle } from "react-icons/fa";

/* ── Theme ── */
const T = {
  bgCard: "#0a1628",
  border: "#1a2d4a",
  accent: "#eab308",
  accentDim: "rgba(234,179,8,0.1)",
  blue: "#3b82f6",
  blueDim: "rgba(59,130,246,0.1)",
  textPrimary: "#f0f6ff",
  textSecondary: "#94a3b8",
  textMuted: "#4b6280",
  success: "#22c55e",
  warning: "#f59e0b",
};

export default function InvoiceModal({ open, onClose, invoice }) {
  const [copied, setCopied] = useState("");

  /* ── original logic preserved exactly ── */
  const canShow = !!invoice;
  const copy = async (val, key) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(key);
      setTimeout(() => setCopied(""), 1200);
    } catch { }
  };

  const shortRef = useMemo(() => {
    if (!invoice?.referenceId) return "";
    return invoice.referenceId.slice(0, 8) + "..." + invoice.referenceId.slice(-6);
  }, [invoice]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
          style={{
            position: "fixed", inset: 0, zIndex: 999,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(2,6,15,0.8)",
            backdropFilter: "blur(8px)",
            padding: 16,
          }}
        >

          <motion.div
            initial={{ scale: 0.92, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.92, y: 24, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 26 }}
            style={{
              width: "100%", maxWidth: 480,
              borderRadius: 28,
              border: `1px solid rgba(234,179,8,0.22)`,
              background: T.bgCard,
              padding: 24,
              color: T.textPrimary,
              boxShadow: "0 32px 90px rgba(0,0,0,0.6), 0 0 50px rgba(234,179,8,0.08)",
              position: "relative", overflow: "hidden",
            }}
          >
            {/* top gold accent line */}
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 3,
              background: `linear-gradient(90deg,${T.accent},${T.blue},transparent)`,
            }} />

            {/* corner glow */}
            <div style={{
              position: "absolute", top: -40, right: -40,
              width: 140, height: 140, borderRadius: "50%",
              background: "radial-gradient(ellipse,rgba(234,179,8,0.08) 0%,transparent 70%)",
              pointerEvents: "none",
            }} />

            {/* ── Header ── */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              borderBottom: `1px solid ${T.border}`, paddingBottom: 18,
              position: "relative", zIndex: 1,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <motion.div
                  initial={{ scale: 0, rotate: -20 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.1 }}
                  style={{
                    width: 46, height: 46, borderRadius: 16,
                    background: `linear-gradient(135deg,#1e40af,${T.blue})`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff",
                    boxShadow: "0 8px 22px rgba(59,130,246,0.35)",
                  }}
                >
                  <FaWallet size={18} />
                </motion.div>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 900, letterSpacing: "-0.01em", margin: 0, color: T.textPrimary }}>
                    Complete Payment
                  </h3>
                  <p style={{ fontSize: 9, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.18em", fontWeight: 800, marginTop: 4 }}>
                    Network: {invoice?.network || "ERC20"}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                style={{
                  width: 36, height: 36, borderRadius: 12,
                  background: "rgba(255,255,255,0.04)",
                  border: `1px solid ${T.border}`,
                  color: T.textMuted, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => { e.currentTarget.style.color = T.accent; e.currentTarget.style.borderColor = T.accent; }}
                onMouseLeave={e => { e.currentTarget.style.color = T.textMuted; e.currentTarget.style.borderColor = T.border; }}
              >
                <FaTimes size={15} />
              </button>
            </div>

            {!canShow ? (
              <div style={{ padding: "48px 0", textAlign: "center", color: T.textMuted, fontSize: 13, fontWeight: 600 }}>
                No active invoice found.
              </div>
            ) : (
              <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 18, position: "relative", zIndex: 1 }}>

                {/* ── Amount Section ── */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15, duration: 0.5 }}
                  style={{
                    position: "relative", overflow: "hidden",
                    borderRadius: 20,
                    background: `linear-gradient(135deg,#92400e,${T.accent})`,
                    padding: 22,
                    boxShadow: "0 12px 32px rgba(234,179,8,0.25)",
                  }}
                >
                  <div style={{ position: "relative", zIndex: 1 }}>
                    <p style={{ fontSize: 10, fontWeight: 800, color: "rgba(10,13,20,0.7)", margin: 0, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                      Total Amount to Send
                    </p>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6 }}>
                      <span style={{ fontSize: "2rem", fontWeight: 900, color: "#0a0d14", letterSpacing: "-0.03em" }}>
                        ${invoice.amountUSD+8}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 900, color: "rgba(10,13,20,0.75)" }}>
                        {invoice.asset || "USDT"}
                      </span>
                    </div>
                    <div style={{
                      marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6,
                      fontSize: 9, background: "rgba(0,0,0,0.18)",
                      padding: "5px 10px", borderRadius: 8,
                      color: "rgba(10,13,20,0.85)", fontWeight: 700,
                    }}>
                      REF: <span style={{ fontFamily: "monospace" }}>{shortRef}</span>
                    </div>
                  </div>
                  {/* decorative blur circle */}
                  <div style={{
                    position: "absolute", right: -16, top: -16,
                    width: 96, height: 96, borderRadius: "50%",
                    background: "rgba(255,255,255,0.15)", filter: "blur(28px)",
                  }} />
                </motion.div>

                {/* ── Address Section ── */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25, duration: 0.5 }}
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  <label style={{
                    fontSize: 9, fontWeight: 900, color: T.textMuted,
                    textTransform: "uppercase", letterSpacing: "0.18em", marginLeft: 4,
                  }}>
                    Destination Address
                  </label>
                  <div style={{
                    position: "relative", borderRadius: 18,
                    border: `1px solid ${T.border}`,
                    background: "rgba(0,0,0,0.3)",
                    padding: 16,
                    transition: "border-color 0.2s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(59,130,246,0.4)"}
                    onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
                  >
                    <p style={{
                      fontFamily: "monospace", fontSize: 13, color: T.textPrimary,
                      lineHeight: 1.6, wordBreak: "break-all", paddingRight: 48, margin: 0,
                    }}>
                      {invoice.address || "Address unavailable"}
                    </p>
                    <button
                      onClick={() => copy(invoice.address || "", "address")}
                      style={{
                        position: "absolute", right: 12, top: 12,
                        width: 40, height: 40, borderRadius: 13,
                        background: copied === "address" ? T.success : T.blue,
                        color: "#fff", border: "none", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: `0 6px 18px ${copied === "address" ? "rgba(34,197,94,0.35)" : "rgba(59,130,246,0.35)"}`,
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.transform = "scale(1.06)"}
                      onMouseLeave={e => e.currentTarget.style.transform = ""}
                    >
                      {copied === "address" ? <FaCheckCircle size={15} /> : <FaCopy size={13} />}
                    </button>
                  </div>
                </motion.div>

                {/* ── Warning ── */}
      {/* ── Warning ── */}
<motion.div
  initial={{ opacity: 0, y: 14 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.35, duration: 0.5 }}
  style={{
    display: "flex",
    gap: 12,
    borderRadius: 16,
    background: T.accentDim,
    border: `1px solid rgba(234,179,8,0.22)`,
    padding: 16,
  }}
>
  <FaInfoCircle 
    style={{ 
      color: T.accent, 
      marginTop: 2, 
      flexShrink: 0 
    }} 
    size={14} 
  />

  <p style={{
    fontSize: 12,
    lineHeight: 1.8,
    color: "#fde68a",
    margin: 0,
    fontWeight: 600,
  }}>

    <strong style={{ color: T.accent }}>
      Payment Notice:
    </strong>

    <br />

    Please send the exact amount shown above:

    <strong style={{ color: "#ffffff" }}>
      {" "}${invoice.amountUSD+8} {invoice.asset || "USDT"}
    </strong>

    <br />

    <span style={{
      color: "#ffcc4d",
      fontWeight: 900,
      fontSize: 13,
    }}>
      + MUST PAY EXTRA $8 USDT
    </span>

    <br /><br />

    The additional $8 USDT is required for network processing fees.

    <br />

    <strong style={{ color: "#ffffff" }}>
      Total payment required:
    </strong>

    <br />

    <span style={{
      color: T.accent,
      fontSize: 14,
      fontWeight: 900,
    }}>
      ${Number(invoice.amountUSD) + 8} USDT
    </span>

    <br /><br />

    Make sure you use the correct network:

    <strong style={{ color: T.accent }}>
      {" "}{invoice.network || "ERC20"}
    </strong>

    . Sending through another network may result in permanent loss of funds.

  </p>
</motion.div>

                {/* ── Footer Status ── */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.45, duration: 0.5 }}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    paddingTop: 6,
                  }}
                >
                  
                  <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                    <span style={{ position: "relative", display: "flex", height: 9, width: 9 }}>
                      <motion.span
                        animate={{ scale: [1, 2.2], opacity: [0.7, 0] }}
                        transition={{ duration: 1.4, repeat: Infinity, ease: "easeOut" }}
                        style={{
                          position: "absolute", inset: 0, borderRadius: "50%",
                          background: T.blue,
                        }}
                      />
                      <span style={{
                        position: "relative", display: "inline-flex",
                        borderRadius: "50%", height: 9, width: 9, background: T.blue,
                      }} />
                    </span>
                    <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 700 }}>
                      Waiting for payment...
                    </span>
                  </div>
                  <div style={{
                    padding: "4px 14px", borderRadius: 999,
                    background: "rgba(255,255,255,0.05)",
                    border: `1px solid ${T.border}`,
                    fontSize: 9, fontWeight: 900,
                    color: T.textSecondary, textTransform: "uppercase", letterSpacing: "0.1em",
                  }}>
                    {invoice.status || "Pending"}
                  </div>
                </motion.div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}